import AntImg from '@/assets/img/ant.png'
import BusImg from '@/assets/img/bus.png'
import GradingState from '@/components/organisms/Grading/GradingState';

interface GradingSpellCheckProps {
  activity?: any
  responseJson?: string
  showTitle?: boolean
}

const GradingSpellCheck = ({ activity, responseJson, showTitle = true }: GradingSpellCheckProps) => {
  // 실제 데이터 파싱
  const spellCheckData = (() => {
    try {
      const item = activity?.items?.[0];
      const content = item?.contentJson ? (typeof item.contentJson === 'string' ? JSON.parse(item.contentJson) : item.contentJson) : {};
      // content 구조 예시는 플래시카드 단어 기반: { wordImageUrl, word }
      // 응답 파싱: 맵/단일 모두 지원
      let parsed: any = {};
      if (responseJson) {
        try { parsed = JSON.parse(responseJson); } catch {}
      }
      const isMapLike = parsed && typeof parsed === 'object' && Object.keys(parsed).some(k => /^\d+$/.test(k));
      const resp = isMapLike ? (parsed['0'] ?? Object.values(parsed)[0] ?? {}) : parsed;
      const correctAnswer: string = content.word || resp.originalWord || '';
      const userAnswer: string = resp.userAnswer || '';
      // 글자 상태: originalWord 대비 userAnswer, 같은 index 맞으면 correct, 없으면 incorrect
      const charStatus: string[] = [];
      for (let i = 0; i < correctAnswer.length; i++) {
        const cc = correctAnswer[i];
        const uc = userAnswer[i] || '';
        if (!uc) charStatus.push('incorrect');
        else if (cc.toLowerCase() === uc.toLowerCase()) charStatus.push('correct');
        else charStatus.push('incorrect');
      }
      const status: 'correct' | 'incorrect' | 'grading' = resp.isCorrect === true ? 'correct' : (resp.isCorrect === false ? 'incorrect' : 'grading');
      const img = content.wordImageUrl || null;
      return [{ id: 1, word: correctAnswer || '-', img, status, userAnswer, correctAnswer, charStatus }];
    } catch (e) {
      console.error('SpellCheck 파싱 실패:', e);
      return [
        { id: 1, word: 'apple', img: null, status: 'grading' as const, userAnswer: '', correctAnswer: 'apple', charStatus: ['grading','grading','grading','grading','grading'] }
      ];
    }
  })();
  
  // 보더 색상을 상태에 따라 반환하는 함수
  const getBorderColor = (status: string) => {
    switch (status) {
      case 'correct':
        return 'border-green-500';
      case 'word_incorrect':
      case 'incorrect':
      case 'both_incorrect':
      case 'recording_incorrect':
        return 'border-red-500';
      case 'grading':
        return 'border-gray-200';
      default:
        return 'border-gray-200';
    }
  };

  // 스펠 체크 결과 렌더링 함수
  const renderSpellCheckResult = (correctAnswer: string, userAnswer: string, charStatus: string[]) => {
    const result = [];
    
    // 정답 단어 길이만큼 반복
    for (let i = 0; i < correctAnswer.length; i++) {
      const correctChar = correctAnswer[i];
      const userChar = userAnswer[i] || '';
      const status = charStatus[i];
      
      if (status) {
        switch (status) {
          case 'correct':
            // 맞은 글자 - green-500 underline
            result.push(<span key={i} className='text-green-500 underline'>{userChar}</span>);
            break;
          case 'empty':
            // 이미 정답이 정해져있는 글자 - gray-900
            result.push(<span key={i} className='text-gray-900'>{correctChar}</span>);
            break;
          case 'incorrect':
            // 빈칸인데 정답을 못맞춘 경우 - red-500 underline
            result.push(<span key={i} className='text-red-500 underline'>{userChar}</span>);
            break;
          case 'grading':
            // 빈칸인데 상위 상태가 채점중일 때 - gray-900 underline
            result.push(<span key={i} className='text-gray-900 underline'>{userChar}</span>);
            break;
          default:
            result.push(<span key={i} className='text-gray-500'>{correctChar}</span>);
        }
      } else {
        // 빈칸인데 정답을 맞춘 경우 - green-500 underline
        result.push(<span key={i} className='text-green-500 underline'>{userChar}</span>);
      }
    }
    return result;
  };
  
  return (
    <div className=''>
    {/* {!problemStatus && (
            <span className='mb-1 text-sm text-red-500'>아직 풀지 않은 문제 입니다.</span>  
        )} */}
        {showTitle && (
          <p className='mb-3 text-lg font-bold'>스펠 체크</p>
        )}
        <div className='flex flex-col gap-2'>
            {spellCheckData.map((item) => (
                <div key={item.id} className={`overflow-hidden rounded-2xl border ${getBorderColor(item.status)}`}>
                    <div className='flex justify-between items-center px-4 py-2'>
                        <div className='flex gap-4 items-center'>
                            <div className="relative size-20 flex-none">
                              {item.img ? (
                                <>
                                  <img 
                                    src={item.img} 
                                    alt="spell-check" 
                                    className='size-20 object-cover rounded-lg' 
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                  <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 font-bold text-[10px] text-center leading-tight rounded-lg">
                                    이미지가<br/>없습니다
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-center justify-center w-full h-full bg-gray-100 text-gray-400 font-bold text-[10px] text-center leading-tight rounded-lg">
                                  이미지가<br/>없습니다
                                </div>
                              )}
                            </div>
                            <p className='text-xl font-bold'>{item.word}</p>
                        </div>
                        {/* 파닉스 유닛 스펠체크: 듣기 버튼 비노출 */}
                    </div>
                    <div className='flex gap-2 justify-between items-center px-5 py-2 bg-gray-50'>
                        <div className='flex gap-2 items-center'>
                            <p className=''>입력한 답</p>
                            <p className='flex gap-px text-xl font-bold'>
                            {renderSpellCheckResult(item.correctAnswer, item.userAnswer, item.charStatus)}
                            </p>
                        </div>
                        <GradingState status={item.status} />
                    </div>
                </div>
            ))}
        </div>
    </div>
  )
}

export default GradingSpellCheck